import { ExecutionContext, html, ref, repeat, when, whenElse } from '@genesislcap/web-core';
import { DealSubmitPreview } from './deal-submit-preview';
import { BondSeries } from '../deal-detail.types';
import { DealSubmitPreviewGrid } from './deal-submit-preview-grid';
import { customEvent } from '@genesislcap/foundation-events';
import { RECONFIRM_REVIEW_SUBMIT_MODAL } from '../../deal-series/deal-series.constants';
import { getSubmitModalMaturityOrders } from '../../deal-series/submit-preview-order.util';
DealSubmitPreviewGrid;

export const dealSubmitPreviewTemplate = html<DealSubmitPreview>`
  ${when(
    (x) => !x.submitInProgress && !x.submitSuccess && !x.error,
    html<DealSubmitPreview>`
      <div class="submit-preview">
        ${repeat(
          (x) => x.bondSeriesList,
          html<BondSeries>`
            <deal-submit-preview-grid
              call-feature-description="${(x) => x.CALL_FEATURE_DESCRIPTION}"
              @selected-orders="${(x, ctx: ExecutionContext<DealSubmitPreview>) =>
                ctx.parent.handleSelectedOrdersChanged(customEvent(ctx), x.SERIES_ID)}"
              ?cancellation="${(x, ctx: ExecutionContext<DealSubmitPreview>) => ctx.parent.cancellation}"
              ?in-reprice-window="${(x, ctx: ExecutionContext<DealSubmitPreview>) => ctx.parent.inRepriceWindow}"
              ?cancel-only-affected-orders="${(x, ctx: ExecutionContext<DealSubmitPreview>) =>
                ctx.parent.cancelOnlyAffectedOrders}"
              ?has-edited-order-in-deal="${(x, ctx: ExecutionContext<DealSubmitPreview>) =>
                ctx.parent.hasAnyEditedOrderInDeal}"
              deal-status="${(x, ctx: ExecutionContext<DealSubmitPreview>) => ctx.parent.dealStatus ?? ''}"
              :series="${(x) => x}"
              :bondSeriesList="${(x, ctx: ExecutionContext<DealSubmitPreview>) =>
                ctx.parent.bondSeriesList}"
              :rowData="${(x, ctx: ExecutionContext<DealSubmitPreview>) =>
                getSubmitModalMaturityOrders(
                  ctx.parent.ordersBySeriesId,
                  ctx.parent.submitOrdersBySeriesId,
                  x.SERIES_ID,
                )}"
            ></deal-submit-preview-grid>
          `,
        )}
        <div class="submit-preview-actions ${(x) => (x.cancellation ? 'submit-preview-actions--cancel-only' : '')}">
          ${when(
            (x) => !x.cancellation,
            html<DealSubmitPreview>`
              <div class="submit-preview-summary" aria-live="polite">
                <div class="summary-row">
                  <span class="summary-label">Total orders to submit ($M):</span>
                  <span class="summary-amount">${(x) => x.submittingSummaryAmount}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Total orders previously submitted ($M):</span>
                  <span class="summary-amount">${(x) => x.previouslySubmittedSummaryAmount}</span>
                </div>
              </div>
            `,
          )}
          <div class="submit-preview-buttons">
            ${whenElse(
              (x) => x.cancellation,
              html<DealSubmitPreview>`
                <rapid-button
                  class="cancel-orders-button"
                  ?disabled="${(x) => !x.isSubmitOrCancelEnabled}"
                  @click="${(x) => x.submitOrders()}"
                >
                  Cancel Selected Orders
                </rapid-button>
              `,
              html<DealSubmitPreview>`
                <rapid-button
                  ?disabled="${(x) => !x.isSubmitOrCancelEnabled}"
                  @click="${(x) => x.handleSubmitClick()}"
                  appearance="primary"
                >
                  Submit Orders
                </rapid-button>
              `,
            )}
            <rapid-button class="submit-preview-close-button" @click="${(x) => x.close()}">Close</rapid-button>
          </div>
        </div>
      </div>
      <rapid-modal
        class="submit-modal"
        :onCloseCallback="${(x) => () => x.dismissReconfirmCta()}"
        @close="${(x, c) => c.event?.stopPropagation()}"
        ${ref('reconfirmCtaModal')}>
        <h3 slot="top">${() => RECONFIRM_REVIEW_SUBMIT_MODAL.TITLE}</h3>
        <div class="reconfirm-modal-content">
          <p>${() => RECONFIRM_REVIEW_SUBMIT_MODAL.BODY_1}</p>
          <div class="reconfirm-modal-actions">
            <rapid-button
              class="reconfirm-cancel-button"
              @click="${(x) => x.dismissReconfirmCta()}">${() => RECONFIRM_REVIEW_SUBMIT_MODAL.BUTTON_CANCEL}
            </rapid-button>
            <rapid-button
              appearance="primary-gradient"
              @click="${(x) => x.confirmReconfirmAll()}">${() => RECONFIRM_REVIEW_SUBMIT_MODAL.BUTTON_CONFIRM}
            </rapid-button>
          </div>
        </div>
      </rapid-modal>
    `,
  )}
  ${when(
    (x) => x.submitInProgress,
    html<DealSubmitPreview>`
      <div class="submit-in-progress">
        <zero-progress-ring min="0" max="100"></zero-progress-ring>
        Processing request...
      </div>
    `,
  )}
  ${when(
    (x) => x.error,
    html<DealSubmitPreview>`
      <div class="submit-in-progress">
        <zero-icon class="${(x) => x.isWarning ? 'submit-warning-icon' : 'submit-failure-icon'}" name="${(x) => x.isWarning ? 'triangle-exclamation' : 'circle-xmark'}" size="xl"></zero-icon>
        ${(x) => x.isWarning ? 'There was a warning' : 'There was an error processing your request'}
      </div>
      ${when(
        (x) => x.errorText,
        html<DealSubmitPreview>`
          <ul>
            ${repeat(
              (x) => x.errorText,
              html<string>`
                <li>${(x) => x}</li>
              `,
            )}
          </ul>
        `,
      )}
      <div class="submit-preview-actions">
        <zero-button appearance="primary-gradient" @click="${(x) => x.submitOrders()}">
          Try again
        </zero-button>
        <zero-button @click="${(x) => x.close()}">Close</zero-button>
      </div>
    `,
  )}
  ${when(
    (x) => x.submitSuccess,
    html<DealSubmitPreview>`
      <div class="submit-result">
        <div class="submit-in-progress">
          <rapid-icon class="submit-success-icon" name="circle-check" size="xl"></rapid-icon>
          ${(x) =>
            x.cancellation
              ? 'Your request has been queued for cancellation.'
              : 'Your request has been queued for submission.'}
        </div>
        <rapid-button class="submit-result-close-button" @click="${(x) => x.close()}">Close</rapid-button>
      </div>
    `,
  )}
`;
